import {ModuleNode, WithHtmlId} from '@backstage-components/base';
import {Alert, AlertIcon, Box} from '@chakra-ui/react';
import {css} from '@emotion/react';
import {FC, Fragment, useMemo} from 'react';
import {VideoPlayerProps} from './Video.interfaces';
import * as Styled from './Video.styled';
import {ComponentDefinition, type SchemaType} from './VideoDefinition';

export type VideoProps = SchemaType & VideoPlayerProps;

export const VideoPreviewComponent: FC<
  ModuleNode<'Video', VideoProps> & WithHtmlId
> = (definition) => {
  const poster = definition.module.props.poster;

  const {
    module: {props, id, slots},
    slotComponent: Slot,
  } = definition;

  // If there are any modules in the "atop" or "between" slots, we'll render
  // even if `useOverlays` is false, so modules don't appear to be orphaned.
  const isOverlay = !!props.isOverlayEnabled;
  const isAbandoned =
    !isOverlay &&
    ((slots?.atop?.length ?? 0) > 0 || (slots?.between?.length ?? 0) > 0);

  const renderedSlots = useMemo(() => {
    if (isAbandoned) {
      return (
        <Alert status="error">
          <AlertIcon />
          Video slots are populated and visible to attendees, but are hidden in
          this admin editor. Tick the "
          {ComponentDefinition.schema.properties.isOverlayEnabled.title}"
          checkbox to show them here so they can be deleted.
        </Alert>
      );
    } else if (isOverlay) {
      return (
        <Fragment>
          <Alert status="info">
            <AlertIcon />
            Non-interactive modules to render under video controls.
          </Alert>
          <Slot slotName="between" />
          <Alert status="info">
            <AlertIcon />
            Interactive modules to render atop video controls.
          </Alert>
          <Slot slotName="atop" />
        </Fragment>
      );
    } else {
      return <Fragment />;
    }
  }, [Slot, isAbandoned, isOverlay]);

  return (
    <Box
      id={id}
      css={css`
        ${props.styleAttr}
      `}
    >
      <Styled.VideoPlaceholder posterUrl={poster} />
      {renderedSlots}
    </Box>
  );
};
